
    import { Component, Vue, Inject } from 'vue-property-decorator'
    import Layout from '../../common/components/layout/layout.vue'
    import { NotificationDispatcher } from '@/app/common/services/notification-dispatcher.service'
    import { InventoryFormRequirement } from '@/app/inventory-form/entities/inventory-form-requirement.entity'
    import { GetInventoryFormRequirementsCommand } from '@/app/inventory-form/commands/get-inventory-form-requirements.command'
    import { GetOcktoFormStateCommand } from '@/app/inventory-form/commands/get-ockto-form-state.command'
    import { SubmitOcktoTestDataCommand } from "@/app/inventory-form/commands/submit-ockto-test-data.command"
    import { RenewQrCodeCommand } from '@/app/inventory-form/commands/renew-qr-code.command'
    import { OcktoFormState } from '@/app/inventory-form/entities/ockto-form-state.entity'
    import { OcktoStateType } from '@/app/inventory-form/support/ockto-state-type'
    import { mapGetters } from "vuex"
    import { ProductAction } from "@/app/product/entities/product-action.entity"
    import { GetAllActionsCommand } from "@/app/product/commands/get-all-actions.command"

    @Component({
        components: { Layout },
        computed: {
            ...mapGetters('product', ['getByProductActionId'])
        },
        metaInfo() {
            return {
                title: 'Ockto form'
            }
        }
    })
    export default class OcktoForm extends Vue {
        state: OcktoFormState|null = null
        productAction?: ProductAction | null = null
        getByProductActionId!: (id: string) => ProductAction | undefined
        requirements: InventoryFormRequirement|null = null
        data = {}
        events: { date: string, event: any }[] = []
        origin = ''
        ocktoStateType = OcktoStateType
        pollTimeout?: number = undefined
        debugLoading = false

        testCases = {
            selected: "TS001_Accelerate_Younger",
            items: [
                { text: 'Client bijna pensioen', value: 'TS001_client' },
                { text: 'Client', value: 'TS001_Accelerate_Younger' },
                { text: 'Partner', value: 'TS001_partner' },
                { text: 'Alleenstaande client', value: 'TS004' },
                { text: '1 Duo lening', value: 'TS002_DUOAppliedSource' },
                { text: '3 Duo leningen', value: 'TS002_DuoLoansMultiple' },
            ]
        }

        @Inject() getAllActionsCommand!: GetAllActionsCommand
        @Inject() getInventoryFormRequirementsCommand!: GetInventoryFormRequirementsCommand
        @Inject() notificationDispatcher!: NotificationDispatcher
        @Inject() getOcktoFormStateCommand!: GetOcktoFormStateCommand
        @Inject() submitOcktoTestDataCommand!: SubmitOcktoTestDataCommand
        @Inject() renewQrCodeCommand!: RenewQrCodeCommand

        async mounted() {
            try {
                const [, requirements] = await Promise.all([
                    this.getAllActionsCommand.run(),
                    this.getInventoryFormRequirementsCommand.run(this.productActionId)
                ])

                this.requirements = requirements
                this.productAction = this.getByProductActionId(this.productActionId) ?? null
                const isTerminal = await this.stateUpdate()
                if (!isTerminal) {
                    this.schedulePoll()
                }
            } catch (err) {
                await this.notificationDispatcher.error(this.getErrorMessage(err))
            }
        }

        async renewQRCode() {
            try {
                await this.renewQrCodeCommand.run(this.productActionId)
                await this.$router.go(0)
            } catch (err) {
                await this.notificationDispatcher.error(this.getErrorMessage(err))
            }
        }

        get ocktoDebug(): boolean {
            return this.requirements?.data?.ockto?.ockto_debug ?? false
        }

        get hasErrors(): boolean {
            const errors = this.requirements?.data?.ockto?.ockto_qrcode?.errors

            if (errors === undefined) {
                return false
            }

            return Object.keys(errors).length !== 0
        }

        get qrCodeImage(): string {
            const base64Qr = this.requirements?.data?.ockto?.ockto_qrcode?.qrCode

            if (base64Qr === undefined || base64Qr === null) {
                return ''
            }

            return `data:image/png;base64,${this.requirements?.data?.ockto?.ockto_qrcode?.qrCode}`
        }

        get deeplinkUrl(): string|null|undefined {
            return this.requirements?.data?.ockto?.ockto_qrcode?.url
        }

        get productActionId(): string {
            return this.$route.params.action
        }

        getErrorMessage(err: unknown): string {
            return err instanceof Error ? err.message : String(err)
        }

        stopPolling() {
            if (this.pollTimeout === undefined) {
                return
            }

            clearTimeout(this.pollTimeout)
            this.pollTimeout = undefined
        }

        async stateUpdate(): Promise<boolean> {
            this.state = await this.getOcktoFormStateCommand.run(this.productActionId)

            switch (this.state?.code) {
                case this.ocktoStateType.start:
                    this.debugLoading = false
                    return false
                case this.ocktoStateType.qrcodeScanned:
                    this.debugLoading = true
                    return false
                case this.ocktoStateType.finished:
                    this.stopPolling()
                    await this.$router.push({ name: 'dashboard' })
                    this.debugLoading = false
                    return true
                default:
                    this.stopPolling()
                    await this.notificationDispatcher.error(this.$t('page.inventory_form.ockto.error_body').toString())
                    await this.$router.push({ name: 'dashboard' })
                    this.debugLoading = false
                    return true
            }
        }

        async submitTestData() {
            try {
                this.debugLoading = true
                await this.submitOcktoTestDataCommand.run(this.testCases.selected, this.productActionId)
            } catch (err) {
                await this.notificationDispatcher.error(this.getErrorMessage(err))
            } finally {
                this.debugLoading = false
            }
        }

        schedulePoll() {
            this.stopPolling()
            this.pollTimeout = window.setTimeout(() => {
                this.pollState()
            }, 5000)
        }

        async pollState() {
            try {
                const isTerminal = await this.stateUpdate()
                if (!isTerminal) {
                    this.schedulePoll()
                }
            } catch (err) {
                this.stopPolling()
                await this.notificationDispatcher.error(this.$t('apiError.genericError').toString())
            }
        }

        destroyed() {
            this.stopPolling()
        }
    }
